/* eslint-disable no-nested-ternary */
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceDot,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
// material
import { Card } from '@mui/material';
//
import moment from 'moment';
import { Fragment } from 'react';
import {
  getMomentAsNumber,
  CustomTooltip,
  CustomReferenceCurrentPosition,
  CustomReferenceDot,
  CustomReferenceDotWarning
} from './flightReviewUtils';
import ButtonUpgradePro from '../ButtonUpgradePro';
import { selectAbonnementData, selectLastFreeFlightUser } from '../../createSelectorUtils';

// ----------------------------------------------------------------------
FlightReviewGraph.propTypes = {
  positions: PropTypes.array,
  analyse: PropTypes.object,
  events: PropTypes.array,
  state: PropTypes.object,
  setState: PropTypes.func,
  indexDisplay: PropTypes.number
};

export default function FlightReviewGraph({
  positions,
  analyse,
  events,
  state,
  setState,
  indexDisplay
}) {
  const abonnementData = useSelector(selectAbonnementData);
  const lastFreeFlightUser = useSelector(selectLastFreeFlightUser);

  const abonnement = abonnementData
    ? true
    : lastFreeFlightUser === null ||
      (positions &&
        positions.length > 0 &&
        positions[0] &&
        new Date(positions[0].fixTime).getTime() <= new Date(lastFreeFlightUser).getTime());

  const CHART_DATA = abonnement
    ? positions.map((position, index) => {
        if (
          analyse.analyseAtterrissageReports &&
          analyse.analyseAtterrissageReports.find(
            (item) =>
              index >=
                positions.indexOf(
                  positions.find((position) => position.id === item.startPositionId)
                ) &&
              index <=
                positions.indexOf(
                  positions.find(
                    (position) =>
                      position.id ===
                      (item.endPositionId === 0 ? item.pointPositionId : item.endPositionId)
                  )
                )
          ) !== undefined
        ) {
          return analyse.analyseAtterrissageReports.find(
            (item) =>
              index ===
              positions.indexOf(positions.find((position) => position.id === item.startPositionId))
          ) !== undefined
            ? {
                ...position,
                altitudeAtterrissage: position.altitude,
                altitudeVol: position.altitude,
                fixTime: getMomentAsNumber(position.fixTime)
              }
            : analyse.analyseAtterrissageReports.find(
                (item) =>
                  item.endPositionId === 0 &&
                  index ===
                    positions.indexOf(
                      positions.find((position) => position.id === item.pointPositionId)
                    )
              ) !== undefined
            ? {
                ...position,
                altitudeAtterrissage: position.altitude,
                altitudeDecollage: position.altitude,
                fixTime: getMomentAsNumber(position.fixTime)
              }
            : analyse.analyseAtterrissageReports.find(
                (item) =>
                  item.endPositionId !== 0 &&
                  index ===
                    positions.indexOf(
                      positions.find((position) => position.id === item.endPositionId)
                    )
              ) !== undefined
            ? {
                ...position,
                altitudeAtterrissage: position.altitude,
                altitudeRoulage: position.altitude,
                fixTime: getMomentAsNumber(position.fixTime)
              }
            : {
                ...position,
                altitudeAtterrissage: position.altitude,
                fixTime: getMomentAsNumber(position.fixTime)
              };
        }
        if (
          analyse.analyseDecollageReports &&
          analyse.analyseDecollageReports.find(
            (item) =>
              index >=
                positions.indexOf(
                  positions.find(
                    (position) =>
                      position.id ===
                      (item.startPositionId === 0 ? item.pointPositionId : item.startPositionId)
                  )
                ) &&
              index <=
                positions.indexOf(positions.find((position) => position.id === item.endPositionId))
          ) !== undefined
        ) {
          return analyse.analyseDecollageReports.find(
            (item) =>
              index ===
              positions.indexOf(positions.find((position) => position.id === item.endPositionId))
          ) !== undefined
            ? {
                ...position,
                altitudeDecollage: position.altitude,
                altitudeVol: position.altitude,
                fixTime: getMomentAsNumber(position.fixTime)
              }
            : analyse.analyseDecollageReports.find(
                (item) =>
                  item.startPositionId === 0 &&
                  index ===
                    positions.indexOf(
                      positions.find((position) => position.id === item.pointPositionId)
                    )
              ) !== undefined
            ? {
                ...position,
                altitudeDecollage: position.altitude,
                altitudeAtterrissage: position.altitude,
                fixTime: getMomentAsNumber(position.fixTime)
              }
            : analyse.analyseDecollageReports.find(
                (item) =>
                  item.startPositionId !== 0 &&
                  index ===
                    positions.indexOf(
                      positions.find((position) => position.id === item.startPositionId)
                    )
              ) !== undefined
            ? {
                ...position,
                altitudeDecollage: position.altitude,
                altitudeRoulage: position.altitude,
                fixTime: getMomentAsNumber(position.fixTime)
              }
            : {
                ...position,
                altitudeDecollage: position.altitude,
                fixTime: getMomentAsNumber(position.fixTime)
              };
        }
        if (
          analyse.analyseVolReports &&
          analyse.analyseVolReports.find(
            (item) =>
              index >=
                positions.indexOf(
                  positions.find((position) => position.id === item.startPositionId)
                ) &&
              index <=
                positions.indexOf(positions.find((position) => position.id === item.endPositionId))
          ) !== undefined
        ) {
          return {
            ...position,
            altitudeVol: position.altitude,
            fixTime: getMomentAsNumber(position.fixTime)
          };
        }
        if (
          analyse.analyseRoulageReports &&
          analyse.analyseRoulageReports.find(
            (item) =>
              index >=
                positions.indexOf(
                  positions.find((position) => position.id === item.startPositionId)
                ) &&
              index <=
                positions.indexOf(positions.find((position) => position.id === item.endPositionId))
          ) !== undefined
        ) {
          return {
            ...position,
            altitudeRoulage: position.altitude,
            fixTime: getMomentAsNumber(position.fixTime)
          };
        }
        return {
          ...position,
          fixTime: getMomentAsNumber(position.fixTime)
        };
      })
    : [];
  return (
    <Card sx={{ height: '35vh', width: '100%', filter: abonnement ? 'none' : 'blur(5px)' }}>
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart
          margin={{ top: 5, left: -50, right: 10, bottom: 0 }}
          data={CHART_DATA}
          onClick={(data) => {
            if (
              data !== undefined &&
              data !== null &&
              data.activeLabel !== undefined &&
              !state.playVol
            ) {
              if (state.selectedListItem !== -1) {
                switch (state.currentPhase) {
                  case 'roulages': {
                    const item = analyse.analyseRoulageReports[state.selectedListItem];
                    if (item !== undefined) {
                      const start = positions.indexOf(
                        positions.find((position) => position.id === item.startPositionId)
                      );
                      const end = positions.indexOf(
                        positions.find((position) => position.id === item.endPositionId)
                      );

                      if (data.activeTooltipIndex >= start && data.activeTooltipIndex <= end) {
                        setState({ ...state, currentPosition: data.activeTooltipIndex });
                      }
                    }
                    break;
                  }
                  case 'decollages': {
                    const item = analyse.analyseDecollageReports[state.selectedListItem];
                    if (item !== undefined) {
                      const start = positions.indexOf(
                        positions.find(
                          (position) =>
                            position.id ===
                            (item.startPositionId === 0
                              ? item.pointPositionId
                              : item.startPositionId)
                        )
                      );
                      const end = positions.indexOf(
                        positions.find((position) => position.id === item.endPositionId)
                      );
                      if (data.activeTooltipIndex >= start && data.activeTooltipIndex <= end) {
                        setState({ ...state, currentPosition: data.activeTooltipIndex });
                      }
                    }

                    break;
                  }
                  case 'vols': {
                    const item = analyse.analyseVolReports[state.selectedListItem];
                    if (item !== undefined) {
                      const start = positions.indexOf(
                        positions.find((position) => position.id === item.startPositionId)
                      );
                      const end = positions.indexOf(
                        positions.find((position) => position.id === item.endPositionId)
                      );

                      if (data.activeTooltipIndex >= start && data.activeTooltipIndex <= end) {
                        setState({ ...state, currentPosition: data.activeTooltipIndex });
                      }
                    }

                    break;
                  }
                  default:
                    setState({ ...state, currentPosition: data.activeTooltipIndex });
                    break;
                }
              } else {
                setState({ ...state, currentPosition: data.activeTooltipIndex });
              }
            }
          }}
        >
          <Tooltip
            wrapperStyle={state.playVol && { visibility: 'visible' }}
            contentStyle={{}}
            content={
              <CustomTooltip
                currentPosition={
                  state.playVol ? positions[indexDisplay] : positions[state.currentPosition]
                }
              />
            }
            position={state.playVol && { x: 5, y: 5 }}
          />

          <CartesianGrid stroke="#000000" />

          <XAxis
            dataKey="fixTime"
            allowDuplicatedCategory={false}
            tickFormatter={(timeStr) => moment(timeStr).format('HH:mm')}
            type="number"
            domain={['auto', 'auto']}
            scale="time"
            tick={false}
          />
          <YAxis
            type="number"
            domain={['dataMin', 'dataMax']}
            tick={false}
            orientation="left"
            yAxisId="alt"
          />
          <Line
            type="dashed"
            dataKey="altitudeRoulage"
            name="altitudeRoulage"
            key="altitudeRoulage"
            dot={false}
            activeDot={{ r: 5 }}
            strokeWidth={state.currentPhase !== 'roulages' && state.currentPhase !== 'all' ? 2 : 3}
            stroke={
              state.currentPhase !== 'roulages' && state.currentPhase !== 'all'
                ? '#bbbbbb'
                : '#000000'
            }
            yAxisId="alt"
          />
          <Line
            type="monotone"
            dataKey="altitudeDecollage"
            name="altitudeDecollage"
            key="altitudeDecollage"
            dot={false}
            activeDot={{ r: 5 }}
            strokeWidth={
              state.currentPhase !== 'decollages' && state.currentPhase !== 'all' ? 2 : 3
            }
            stroke={
              state.currentPhase !== 'decollages' && state.currentPhase !== 'all'
                ? '#bbbbbb'
                : '#22398d'
            }
            yAxisId="alt"
          />
          <Line
            type="monotone"
            dataKey="altitudeVol"
            name="altitudeVol"
            key="altitudeVol"
            dot={false}
            activeDot={{ r: 5 }}
            strokeWidth={state.currentPhase !== 'vols' && state.currentPhase !== 'all' ? 2 : 3}
            stroke={
              state.currentPhase !== 'vols' && state.currentPhase !== 'all' ? '#bbbbbb' : '#095228'
            }
            yAxisId="alt"
          />
          <Line
            type="monotone"
            dataKey="altitudeAtterrissage"
            name="altitudeAtterrissage"
            key="altitudeAtterrissage"
            dot={false}
            activeDot={{ r: 5 }}
            strokeWidth={
              state.currentPhase !== 'atterrissages' && state.currentPhase !== 'all' ? 2 : 3
            }
            stroke={
              state.currentPhase !== 'atterrissages' && state.currentPhase !== 'all'
                ? '#bbbbbb'
                : '#e50e58'
            }
            yAxisId="alt"
          />

          {(analyse.analyseAtterrissageReports.length !== analyse.analyseDecollageReports.length ||
            (analyse.analyseAtterrissageReports.length === 0 &&
              analyse.analyseDecollageReports.length === 0)) && (
            <Line
              type="monotone"
              dataKey="altitude"
              name="altitude"
              key="altitude"
              dot={false}
              activeDot={{ r: 5 }}
              strokeWidth={3}
              stroke="#800080"
              yAxisId="alt"
            />
          )}

          <Area
            type="monotone"
            dataKey="attributes.elevation"
            stroke="#c8ad7f"
            fill="#c8ad7f"
            dot={false}
            activeDot={{ r: 5 }}
            strokeWidth={3}
            yAxisId="alt"
          />
          <ReferenceDot
            key={`CurrentPosition${state.playVol ? indexDisplay : state.currentPosition}`}
            x={getMomentAsNumber(
              positions[state.playVol ? indexDisplay : state.currentPosition] &&
                positions[state.playVol ? indexDisplay : state.currentPosition].fixTime
            )}
            y={
              positions[state.playVol ? indexDisplay : state.currentPosition] &&
              positions[state.playVol ? indexDisplay : state.currentPosition].altitude
            }
            yAxisId="alt"
            shape={CustomReferenceCurrentPosition}
          />
          {state.currentPhase === 'vols' &&
            state.selectedListItem !== -1 &&
            (() => {
              const item = analyse.analyseVolReports[state.selectedListItem];
              if (item !== undefined) {
                const start = positions.indexOf(
                  positions.find((position) => position.id === item.startPositionId)
                );
                const end = positions.indexOf(
                  positions.find((position) => position.id === item.endPositionId)
                );
                const alertSpeed =
                  item.speedAlert !== 0
                    ? positions.indexOf(
                        positions.find((position) => position.id === item.speedAlert)
                      )
                    : 0;

                const alertG =
                  item.gAlert !== 0
                    ? positions.indexOf(positions.find((position) => position.id === item.gAlert))
                    : 0;

                const entersGeofences = events
                  .filter(
                    (event) =>
                      event.type === 'geofenceEnter' &&
                      positions.indexOf(
                        positions.find((position) => position.id === event.positionId)
                      ) >= start &&
                      positions.indexOf(
                        positions.find((position) => position.id === event.positionId)
                      ) <= end
                  )
                  .map((event) =>
                    positions.indexOf(positions.find((item) => item.id === event.positionId))
                  );
                const exitsGeofences = events
                  .filter(
                    (event) =>
                      event.type === 'geofenceExit' &&
                      positions.indexOf(
                        positions.find((position) => position.id === event.positionId)
                      ) >= start &&
                      positions.indexOf(
                        positions.find((position) => position.id === event.positionId)
                      ) <= end
                  )
                  .map((event) =>
                    positions.indexOf(positions.find((item) => item.id === event.positionId))
                  );
                if (state.currentPosition >= start && state.currentPosition <= end) {
                  return (
                    <Fragment key={`Vol ${state.selectedListItem}`}>
                      {entersGeofences.map(
                        (enterGeofences, indexGeofence) =>
                          enterGeofences === state.currentPosition && (
                            <ReferenceDot
                              key={`EnterGeofence ${indexGeofence}`}
                              x={getMomentAsNumber(positions[enterGeofences].fixTime)}
                              y={positions[enterGeofences].altitude}
                              shape={CustomReferenceDot}
                              yAxisId="alt"
                            />
                          )
                      )}
                      {events.map((event, i) => {
                        if (
                          event.type === 'geofenceEnter' &&
                          positions.indexOf(
                            positions.find((position) => position.id === event.positionId)
                          ) === state.currentPosition &&
                          i + 1 < events.length
                        ) {
                          const exitGeofences = positions.indexOf(
                            positions.find((position) => position.id === events[i + 1].positionId)
                          );
                          if (exitsGeofences.includes(exitGeofences)) {
                            return (
                              <ReferenceDot
                                key={`ExitGeofence ${exitGeofences}`}
                                x={getMomentAsNumber(positions[exitGeofences].fixTime)}
                                y={positions[exitGeofences].altitude}
                                shape={CustomReferenceDot}
                                yAxisId="alt"
                              />
                            );
                          }
                        }
                        return null;
                      })}

                      {alertSpeed !== 0 && alertSpeed === state.currentPosition && (
                        <ReferenceDot
                          key={`alertSpeed ${state.selectedListItem}`}
                          x={getMomentAsNumber(positions[alertSpeed].fixTime)}
                          y={positions[alertSpeed].altitude}
                          shape={CustomReferenceDot}
                          yAxisId="alt"
                        />
                      )}
                      {alertG !== 0 && alertG === state.currentPosition && (
                        <ReferenceDot
                          key={`alertG ${state.selectedListItem}`}
                          x={getMomentAsNumber(positions[alertG].fixTime)}
                          y={positions[alertG].altitude}
                          shape={CustomReferenceDot}
                          yAxisId="alt"
                        />
                      )}
                    </Fragment>
                  );
                }
              }
              return null;
            })}

          {state.currentPhase === 'decollages' &&
            state.selectedListItem !== -1 &&
            (() => {
              const item = analyse.analyseDecollageReports[state.selectedListItem];
              if (item !== undefined) {
                const start = positions.indexOf(
                  positions.find(
                    (position) =>
                      position.id ===
                      (item.startPositionId === 0 ? item.pointPositionId : item.startPositionId)
                  )
                );
                const end = positions.indexOf(
                  positions.find((position) => position.id === item.endPositionId)
                );

                const alertSpeed =
                  item.speedAlert !== 0
                    ? positions.indexOf(
                        positions.find((position) => position.id === item.speedAlert)
                      )
                    : 0;
                if (state.currentPosition >= start && state.currentPosition <= end) {
                  return (
                    <Fragment key={`Decollage ${state.selectedListItem}`}>
                      {alertSpeed !== 0 && state.currentPosition === alertSpeed && (
                        <ReferenceDot
                          key={`alertSpeed ${state.selectedListItem}`}
                          x={getMomentAsNumber(positions[alertSpeed].fixTime)}
                          y={positions[alertSpeed].altitude}
                          shape={CustomReferenceDot}
                          yAxisId="alt"
                        />
                      )}
                    </Fragment>
                  );
                }
              }
              return null;
            })()}
          {state.currentPhase === 'roulages' && state.selectedListItem !== -1
            ? (() => {
                const item = analyse.analyseRoulageReports[state.selectedListItem];
                if (item !== undefined) {
                  const start = positions.indexOf(
                    positions.find((position) => position.id === item.startPositionId)
                  );
                  const end = positions.indexOf(
                    positions.find((position) => position.id === item.endPositionId)
                  );
                  const alertSpeed =
                    item.speedAlert !== 0
                      ? positions.indexOf(
                          positions.find((position) => position.id === item.speedAlert)
                        )
                      : 0;
                  const alertG =
                    item.gAlert !== 0
                      ? positions.indexOf(positions.find((position) => position.id === item.gAlert))
                      : 0;
                  const warningG =
                    item.gWarning !== 0
                      ? positions.indexOf(
                          positions.find((position) => position.id === item.gWarning)
                        )
                      : 0;

                  if (state.currentPosition >= start && state.currentPosition <= end) {
                    return (
                      <Fragment key={`Roulage ${state.selectedListItem}`}>
                        {alertSpeed !== 0 && alertSpeed === state.currentPosition && (
                          <ReferenceDot
                            key={`alertSpeed ${state.selectedListItem}`}
                            x={getMomentAsNumber(positions[alertSpeed].fixTime)}
                            y={positions[alertSpeed].altitude}
                            shape={CustomReferenceDot}
                            yAxisId="alt"
                          />
                        )}
                        {alertG !== 0 && alertG === state.currentPosition && (
                          <ReferenceDot
                            key={`alertG ${state.selectedListItem}`}
                            x={getMomentAsNumber(positions[alertG].fixTime)}
                            y={positions[alertG].altitude}
                            shape={CustomReferenceDot}
                            yAxisId="alt"
                          />
                        )}
                        {warningG !== 0 && warningG === state.currentPosition && (
                          <ReferenceDot
                            key={`warningG ${state.selectedListItem}`}
                            x={getMomentAsNumber(positions[warningG].fixTime)}
                            y={positions[warningG].altitude}
                            shape={CustomReferenceDotWarning}
                            yAxisId="alt"
                          />
                        )}
                      </Fragment>
                    );
                  }
                }
                return null;
              })()
            : null}
        </ComposedChart>
      </ResponsiveContainer>
      {!abonnement && <ButtonUpgradePro />}
    </Card>
  );
}
